import React from "react";

import { Close, Error, ExpandLess, ExpandMore, Help, Warning } from "@mui/icons-material";

import { resolveClassNames } from "@lib/utils/resolveClassNames";
import { getTextWidthWithFont, measureTextWidthWithElement } from "@lib/utils/textSize";

import type { TreeNodeSelection } from "../private-utils/treeNodeSelection";

type TagProps = {
    key: string;
    index: number;
    placeholder: string;
    treeNodeSelection: TreeNodeSelection;
    countTags: number;
    currentTag: boolean;
    frameless: boolean;
    active: boolean;
    isDuplicate: boolean;
    inputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    inputKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    inputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    inputSelect: (e: React.SyntheticEvent<HTMLInputElement, Event>, index: number) => void;
    inputBlur: (index: number) => void;
    hideSuggestions: (callback?: () => void) => void;
    removeTag: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => void;
    updateSelectedTagsAndNodes: () => void;
    shake: boolean;
    maxNumSelectedNodes: number;
    disabled?: boolean;
};

/**
 * A component for displaying and interacting with a tag.
 */
export class Tag extends React.Component<TagProps> {
    props: TagProps;
    state: { hovered: boolean };

    constructor(props: TagProps) {
        super(props);

        this.props = props;
        this.state = { hovered: false };
    }

    componentDidMount(): void {
        this.forceUpdate();
    }

    private addAdditionalClasses(invalid: boolean): boolean {
        const { currentTag, treeNodeSelection } = this.props;
        return (
            treeNodeSelection.displayAsTag() ||
            (!invalid && !currentTag) ||
            (invalid && !currentTag && treeNodeSelection.getNodeName(0) !== "")
        );
    }

    private innerTagClasses(invalid = false, duplicate = false): string {
        let ret = {
            "text-body-sm flex flex-wrap rounded justify-left items-center min-w-0 border-2 border-transparent whitespace-pre-wrap z-elevated": true,
        };
        if (this.addAdditionalClasses(invalid)) {
            ret = Object.assign({}, ret, {
                [invalid ? "bg-danger-canvas" : duplicate ? "bg-warning-canvas" : "border-transparent"]: true,
            });
        }
        return resolveClassNames(ret);
    }

    private outerTagClasses(invalid: boolean, duplicate: boolean, frameless: boolean): string {
        return resolveClassNames(
            "flex flex-wrap rounded justify-left items-center min-w-0 relative whitespace-pre-wrap",
            {
                SmartNodeSelector__Tag: this.displayAsTag() || frameless,
                "bg-neutral": this.displayAsTag(),
                "border-transparent bg-transparent": !this.displayAsTag() && !frameless,
                animate__animated: this.props.shake,
                animate__headShake: this.props.shake,
                [!this.addAdditionalClasses(invalid)
                    ? ""
                    : invalid
                      ? "bg-danger-canvas!"
                      : duplicate
                        ? "bg-warning-canvas!"
                        : ""]: true,
            },
        );
    }

    private calculateTextWidth(text: string, padding = 10, minWidth = 50): number {
        if (text === undefined) {
            text = "";
        }

        const width = getTextWidthWithFont(text, "Equinor", 0.875);
        return Math.max(minWidth, width + padding);
    }

    private createMatchesCounter(nodeSelection: TreeNodeSelection, index: number): JSX.Element | null {
        if (nodeSelection.containsWildcard() && nodeSelection.countExactlyMatchedNodePaths() > 0) {
            const matches = nodeSelection.countExactlyMatchedNodePaths();
            let fromMax = "";
            let title = "This expression matches " + matches + " options.";
            if (this.props.maxNumSelectedNodes !== -1 && matches > this.props.maxNumSelectedNodes) {
                fromMax = " / " + this.props.maxNumSelectedNodes;
                title = `This expression matches ${matches} node${matches !== 1 && "s"}, but only ${
                    this.props.maxNumSelectedNodes
                } can be selected.`;
            }

            return (
                <span
                    key={"TagMatchesCounter_" + index}
                    className={resolveClassNames(
                        "mr-2xs px-3xs text-accent-strong-on-emphasis text-body-xs relative flex h-5 min-w-5 items-center justify-center rounded-full text-center leading-none outline-hidden",
                        {
                            "bg-info-strong text-info-strong-on-emphasis":
                                matches <= this.props.maxNumSelectedNodes || this.props.maxNumSelectedNodes === -1,
                            "bg-warning-strong text-warning-strong-on-emphasis":
                                matches > this.props.maxNumSelectedNodes && this.props.maxNumSelectedNodes !== -1,
                        },
                    )}
                    title={title}
                >
                    {matches}
                    {fromMax}
                </span>
            );
        }
        return null;
    }

    private createBrowseButtons(nodeSelection: TreeNodeSelection, index: number): React.ReactNode | null {
        const { currentTag } = this.props;
        if (
            ((nodeSelection.isValidUpToFocussedNode() && currentTag) || nodeSelection.isValid()) &&
            !nodeSelection.containsWildcard() &&
            this.displayAsTag() &&
            nodeSelection.countAvailableChildNodes(nodeSelection.getFocusedLevel() - 1) > 1
        ) {
            const subgroups = nodeSelection
                .availableChildNodes(nodeSelection.getFocusedLevel() - 1)
                .map((data) => data.nodeName);
            let position = subgroups.indexOf(nodeSelection.getFocusedNodeName());
            if (position === -1) {
                position = 0;
            }
            return (
                <div
                    key={"TagBrowseButton_" + index}
                    className="text-body-xs mr-3xs border-r-neutral flex h-full max-h-6 w-4 flex-col border-r"
                >
                    <button
                        key={"TagPreviousButton_" + index}
                        className={resolveClassNames(
                            "bg-accent text-body-2xs m-0 flex h-1/2 w-full cursor-pointer appearance-none flex-col items-center justify-center p-0 outline-hidden disabled:cursor-default disabled:opacity-30",
                            { "hover:bg-accent-hover": position !== 0 },
                        )}
                        disabled={position === 0}
                        title="Previous option"
                        onMouseDown={(e): void => this.shiftOption(e, nodeSelection, false)}
                        onMouseUp={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <ExpandLess fontSize="inherit" />
                    </button>
                    <button
                        key={"TagNextButton_" + index}
                        className={resolveClassNames(
                            "bg-accent text-body-2xs m-0 flex h-1/2 w-full cursor-pointer appearance-none flex-col items-center justify-center p-0 outline-hidden disabled:cursor-default disabled:opacity-30",
                            { "hover:bg-accent-hover": position !== subgroups.length - 1 },
                        )}
                        disabled={position === subgroups.length - 1}
                        title="Next option"
                        onMouseDown={(e): void => this.shiftOption(e, nodeSelection, true)}
                        onMouseUp={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <ExpandMore fontSize="inherit" />
                    </button>
                </div>
            );
        }
        return null;
    }

    private shiftOption(e: React.MouseEvent<HTMLButtonElement>, nodeSelection: TreeNodeSelection, up: boolean): void {
        e.preventDefault();
        e.stopPropagation();
        const { hideSuggestions, updateSelectedTagsAndNodes } = this.props;
        const inputElement = (nodeSelection.getRef() as React.RefObject<HTMLInputElement>).current as HTMLInputElement;
        const currentSelection = [inputElement.selectionStart, inputElement.selectionEnd];
        const subgroups = nodeSelection
            .availableChildNodes(nodeSelection.getFocusedLevel() - 1)
            .map((el) => el.nodeName);
        const newPosition = subgroups.indexOf(nodeSelection.getFocusedNodeName()) + (up ? 1 : -1);
        if (!up && newPosition < 0) return;
        if (up && newPosition >= subgroups.length) return;
        nodeSelection.setNodeName(subgroups[newPosition]);
        hideSuggestions(() => {
            if (currentSelection[0] !== null && currentSelection[1] !== null) {
                window.setTimeout(() => {
                    inputElement.setSelectionRange(currentSelection[0], currentSelection[1]);
                }, 20);
            }
        });
        updateSelectedTagsAndNodes();
        this.forceUpdate();
    }

    private tagTitle(nodeSelection: TreeNodeSelection, index: number): string {
        const { countTags, isDuplicate, maxNumSelectedNodes } = this.props;
        if (
            index === countTags - 1 &&
            !nodeSelection.displayAsTag() &&
            nodeSelection.displayText() === "" &&
            maxNumSelectedNodes !== 1
        ) {
            return "Enter a new name";
        } else if (!nodeSelection.isValid()) {
            return "Invalid";
        } else if (isDuplicate) {
            return "Duplicate";
        } else if (!nodeSelection.isComplete()) {
            return "Incomplete";
        } else {
            const exactlyMatchedNodePaths = nodeSelection.exactlyMatchedNodePaths();
            if (maxNumSelectedNodes === -1 || exactlyMatchedNodePaths.length <= maxNumSelectedNodes) {
                return exactlyMatchedNodePaths.join("\n");
            }
            return `Matched ${exactlyMatchedNodePaths.length} node${
                exactlyMatchedNodePaths.length !== 1 && "s"
            } but only ${maxNumSelectedNodes} ${
                maxNumSelectedNodes === 1 ? "is" : "are"
            } allowed.\n\nSelected nodes:\n${exactlyMatchedNodePaths.slice(0, maxNumSelectedNodes).join("\n")}`;
        }
    }

    private handleInput(e: React.FormEvent<HTMLInputElement>): void {
        const val = (e.target as HTMLInputElement).value;
        if (val) {
            (e.target as HTMLInputElement).style.width = this.calculateTextWidth(val) + "px";
        }
    }

    private displayAsTag(): boolean {
        const { treeNodeSelection, currentTag } = this.props;

        return (
            treeNodeSelection.displayAsTag() ||
            (treeNodeSelection.isValid() && currentTag) ||
            (treeNodeSelection.getNodeName(0) !== "" && !currentTag)
        );
    }

    private createFocusOverlay(treeNodeSelection: TreeNodeSelection): React.ReactNode | null {
        const value = treeNodeSelection.displayText();
        if (!value) return null;

        const inputElement = (treeNodeSelection.getRef() as React.RefObject<HTMLInputElement>).current;
        if (!inputElement) return null;

        const delimiter = treeNodeSelection.getDelimiter();
        const segments = value.split(delimiter);
        const focusedIdx =
            segments.length > 1 ? treeNodeSelection.getFocusedLevel() - treeNodeSelection.getNumMetaNodes() : 0;

        const prefix = segments.slice(0, focusedIdx).join(delimiter);
        const focused = segments[focusedIdx] ?? "";

        const left = measureTextWidthWithElement(prefix ? prefix + delimiter : "", inputElement);
        const width = measureTextWidthWithElement(focused, inputElement);

        return (
            <div
                className="border-b-info-strong absolute bottom-0 h-0.5 border-b border-dashed"
                style={{ left: `${left}px`, width: `${width}px` }}
            />
        );
    }

    private calculateInputWidth(): string {
        const { treeNodeSelection } = this.props;
        const displayText = treeNodeSelection.displayText();

        if (treeNodeSelection.getFocusedNodeName() === "" && treeNodeSelection.getFocusedLevel() == 0) {
            return this.calculateTextWidth(this.props.placeholder) + "px";
        } else {
            return this.calculateTextWidth(displayText) + "px";
        }
    }

    private makeStyle(): { [key: string]: string | number } {
        const { treeNodeSelection, frameless } = this.props;

        const colors = treeNodeSelection.colors();
        const style: { [key: string]: string } = {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "transparent",
        };

        if (colors.length >= 2) {
            style["background"] = `linear-gradient(to left, ${colors.join(", ")}) border-box`;
            style["borderColor"] = "transparent";
        } else if (colors.length === 1) {
            style["borderColor"] = colors[0];
        }

        if (frameless) {
            style["flex"] = "1";
        }

        return style;
    }

    private handleClickEvent(e: React.MouseEvent<HTMLLIElement>): void {
        const input = (this.props.treeNodeSelection.getRef() as React.RefObject<HTMLInputElement>)
            .current as HTMLInputElement;
        if (input) {
            input.focus();
            e.preventDefault();
            e.stopPropagation();
        }
    }

    render(): React.ReactNode {
        const {
            index,
            treeNodeSelection,
            currentTag,
            frameless,
            active,
            isDuplicate,
            inputKeyDown,
            inputKeyUp,
            inputChange,
            inputSelect,
            inputBlur,
            removeTag,
            disabled,
        } = this.props;

        const displayText = treeNodeSelection.displayText();

        const valid = treeNodeSelection.isValid();

        let placeholder = "";
        if (treeNodeSelection.getFocusedNodeName() === "" && treeNodeSelection.getFocusedLevel() === 0 && !active) {
            placeholder = this.props.placeholder;
        }

        return (
            <li
                key={"Tag_" + index}
                title={this.tagTitle(treeNodeSelection, index)}
                className={this.outerTagClasses(!valid && !currentTag, isDuplicate, frameless)}
                style={this.makeStyle()}
                onMouseEnter={(): void => this.setState({ hovered: true })}
                onMouseLeave={(): void => this.setState({ hovered: false })}
                onClick={(e): void => this.handleClickEvent(e)}
            >
                {this.displayAsTag() && !frameless && !disabled && (
                    <button
                        type="button"
                        key={"TagRemoveButton_" + index}
                        className="z-overlay bg-accent selectable text-body-xs absolute -top-2 -right-2.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full"
                        title="Remove"
                        onClick={(e): void => removeTag(e, index)}
                    >
                        <Close fontSize="inherit" className="text-white" />
                    </button>
                )}
                {this.createBrowseButtons(treeNodeSelection, index)}
                <div key={"InnerTag_" + index} className={this.innerTagClasses(!valid && !currentTag, isDuplicate)}>
                    {this.addAdditionalClasses(!valid) && !valid && !currentTag && (
                        <Error fontSize="small" className="mr-3xs" />
                    )}
                    {this.addAdditionalClasses(!valid) && valid && isDuplicate && (
                        <Warning fontSize="small" className="mr-3xs" />
                    )}
                    {this.addAdditionalClasses(!valid) &&
                        (valid || currentTag) &&
                        !isDuplicate &&
                        treeNodeSelection.icons().length > 1 && <Help fontSize="small" className="mr-3xs" />}
                    {(valid || currentTag) && !isDuplicate && treeNodeSelection.icons().length === 1 && (
                        <span className="mr-xs flex h-4 w-4 shrink-0 items-center justify-center">
                            {treeNodeSelection.icons()[0]}
                        </span>
                    )}
                    {this.createMatchesCounter(treeNodeSelection, index)}
                    <div className="relative flex whitespace-nowrap">
                        <input
                            className="text-body-sm inline-block w-12 border-0 bg-transparent p-0 outline-hidden"
                            spellCheck="false"
                            key={"TagInput_" + index}
                            type="text"
                            placeholder={placeholder}
                            value={displayText}
                            style={{
                                width: this.calculateInputWidth(),
                            }}
                            ref={treeNodeSelection.getRef()}
                            disabled={disabled}
                            onInput={(e): void => this.handleInput(e)}
                            onClick={(e): void => e.stopPropagation()}
                            onChange={(e): void => inputChange(e)}
                            onKeyUp={(e): void => inputKeyUp(e)}
                            onKeyDown={(e): void => inputKeyDown(e)}
                            onSelect={(e): void => inputSelect(e, index)}
                            onBlur={(): void => inputBlur(index)}
                        />
                        {(currentTag || this.state.hovered) &&
                            !treeNodeSelection.isSelected() &&
                            this.createFocusOverlay(treeNodeSelection)}
                    </div>
                    {treeNodeSelection.isSelected() && (
                        <div
                            key={"TagSelected_" + index}
                            className="z-elevated bg-accent-active absolute top-0 left-0 block h-full w-full rounded-sm opacity-60"
                        ></div>
                    )}
                </div>
            </li>
        );
    }
}
